import { Inject, Injectable } from '@angular/core';
import { arrayUpsert } from '@datorama/akita';
import { HttpClient } from '@angular/common/http';
import { map, retryWhen, switchMap, tap } from 'rxjs/operators';
import { StrapiCollectionResponse, StrapiEntity } from '../global/global.model';
import { environment } from '../../../../environments/environment';
import qs from 'qs';
import { Project, ProjectsQuery, ProjectsStore } from '../project';
import { PublicEncodeJob } from '../../../../../../api/src/encode/encode-job.entity';
import { ReviewerRole } from '../../../pages/review/review-reviewers-dialog/review-reviewers-dialog.component';
import { AddReviewersDto } from '../../../../../../api/src/project/models/add-reviewers.dto';
import { RemoveReviewersDto } from '../../../../../../api/src/project/models/remove-reviewers.dto';
import { UpdateReviewerDto } from '../../../../../../api/src/project/models/update-reviewer.dto';
import { AddCommentDto } from '../../../../../../api/src/comment/dtos/add-comment.dto';
import { DefaultResponse } from '../session';
import { QueryParams } from '../collection/collection.model';
import { TextSingularPluralPipe } from '../../../shared/pipes/text-singular-plural.pipe';
import { ExportTypes } from '../../../../../../api/src/creative-unit-package/creative-unit-package.entity';
import { PublicComment } from '../../../../../../api/src/comment/comment.entity';
import { stringToFilename } from '../../utils/string.utils';
import { CartItem } from '../cart/cart.model';
import { removeObjectValues } from '../../utils/object.utils';
import { DOCUMENT } from '@angular/common';
import { GlobalService } from '../global';
import { SseClientService } from '../../services/sse-client.service';
import { from, of, throwError, timer } from 'rxjs';
import { TaskStatus } from '../../../../../../api/src/encode/encode-task.entity';
import { ExportType } from '../../../../../../api/src/encode/models/job-def.dto';

@Injectable({ providedIn: 'root' })
export class EncodeJobService {
	constructor(
		private projectsStore: ProjectsStore,
		private projectsQuery: ProjectsQuery,
		private globalService: GlobalService,
		private http: HttpClient,
		private textSingularPluralPipe: TextSingularPluralPipe,
		private sse: SseClientService,
		@Inject(DOCUMENT) private document: Document
	) {}

	get() {
		return this.http
			.get<DefaultResponse<PublicEncodeJob[]>>(`${environment.apiUrl}/encode/list/${this.projectsQuery.getActiveId()}`)
			.pipe(
				map(response => ({
					...response,
					data: response.data
				}))
			);
	}

	getExportHistoryDetails(projectId: string, encodeJobId: string) {
		return this.http
			.get<DefaultResponse<any>>(`${environment.apiUrl}/encode/export-history/${projectId}/${encodeJobId}`)
			.pipe(map(response => response.data));
	}

	/**
	 * Lightweight status check for export polling - only loads essential data
	 * to reduce memory usage during frequent status checks on Heroku.
	 */
	getEncodeJobStatus(jobId: string) {
		return this.http.get<any>(`${environment.apiUrl}/encode/status/${jobId}`).toPromise();
	}

	findExportTableData({ page, limit, sortBy, query }: QueryParams) {
		const sortOption: any = {};
		if (sortBy) {
			sortOption.sortBy = sortBy.id;
			sortOption.sort = sortBy.direction;
		}

		if (query) {
			sortOption.query = query;
		}

		return this.http
			.get<DefaultResponse<PublicEncodeJob[]>>(`${environment.apiUrl}/encode/export-history/${this.projectsQuery.getActiveId()}`, {
				params: { page, limit, ...sortOption }
			})
			.pipe(
				map(response => {
					return {
						...response,
						data: response.data.map(job => {
							const metadatas = job.encodeTasks?.map(task => task.metadata) || [];

							const videos = metadatas.filter(metadata => metadata.isVideo) || [];
							const ads = metadatas.filter(metadata => metadata.isAd) || [];

							let videoText: string, adText: string;
							if (videos.length > 0) {
								videoText = this.textSingularPluralPipe.transform(
									videos.length,
									$localize`:Export History Creative Packages Content@@exportHistoryVideoLabel:Video`,
									$localize`:Export History Creative Packages Content@@exportHistoryVideosLabel:Videos`
								);
							}
							if (ads.length > 0) {
								adText = this.textSingularPluralPipe.transform(
									ads.length,
									$localize`:Export History Creative Packages Content@@exportHistoryAdLabel:Size`,
									$localize`:Export History Creative Packages Content@@exportHistoryAdsLabel:Sizes`
								);
							}

							let formats =
								metadatas.flatMap(metadata => {
									return (metadata?.formats || [ExportType.VIDEO]).map(format =>
										ExportTypes.getExportTypeFileFormat(format, metadata.outputConfig)
									);
								}) || [];

							return {
								packages: [videoText, adText].filter(Boolean).join(', '),
								formats: [...new Set(formats)].join(', '),
								...job
							};
						})
					};
				})
			);
	}

	public startReview(projectId: string) {
		return this.http.post<DefaultResponse<Project>>(`${environment.apiUrl}/projects/${projectId}/review/start`, {}).pipe(
			tap(response => {
				this.projectsStore.upsert(response.data.id, response.data);
			})
		);
	}

	public retryJob(jobId: string) {
		return this.http.post(`${environment.apiUrl}/encode/job/${jobId}/retry`, {}).pipe(
			tap(response => {
				console.log(response);
			})
		);
	}

	public retryTasks(jobId: string) {
		return this.http.post(`${environment.apiUrl}/encode/job/${jobId}/retry?force=true`, {}).pipe(
			tap(response => {
				console.log(response);
			})
		);
	}

	public cancelJob(jobId: string) {
		return this.http.post(`${environment.apiUrl}/encode/job/${jobId}/cancel`, {}).pipe(
			tap(response => {
				console.log(response);
			})
		);
	}

	public retryTask(taskId: string) {
		return this.http.post(`${environment.apiUrl}/encode/task/${taskId}/retry`, {}).pipe(
			tap(response => {
				console.log(response);
			})
		);
	}

	public addReviewers(project: Project, encodeJob: PublicEncodeJob, emails: string[], role: ReviewerRole) {
		this.projectsStore.setLoading(true);

		const params: AddReviewersDto = {
			[role + 's']: {
				emails
			}
		};

		return this.http
			.put<DefaultResponse<PublicEncodeJob>>(
				`${environment.apiUrl}/projects/${project.id}/review/job/${encodeJob.id}/viewers`,
				params
			)
			.pipe(
				tap(response => {
					this.projectsStore.upsert(response.data.projectId, {
						encodeJobs: arrayUpsert(
							this.projectsQuery.getEntity(response.data.projectId).encodeJobs,
							response.data.id,
							response.data
						)
					});
					this.projectsStore.setLoading(false);
				})
			);
	}

	public updateReviewer(project: Project, encodeJob: PublicEncodeJob, reviewerId: string, isApprover: boolean) {
		this.projectsStore.setLoading(true);

		const params: UpdateReviewerDto = {
			isApprover
		};

		return this.http
			.put<DefaultResponse<PublicEncodeJob>>(
				`${environment.apiUrl}/projects/${project.id}/review/job/${encodeJob.id}/viewers/${reviewerId}`,
				params
			)
			.pipe(
				tap(response => {
					console.log('updateReviewer', response);
					this.projectsStore.upsert(response.data.projectId, {
						encodeJobs: arrayUpsert(
							this.projectsQuery.getEntity(response.data.projectId).encodeJobs,
							response.data.id,
							response.data
						)
					});
					this.projectsStore.setLoading(false);
				})
			);
	}

	public removeReviewers(project: Project, encodeJob: PublicEncodeJob, ids: string[], role: ReviewerRole) {
		this.projectsStore.setLoading(true);

		const params: RemoveReviewersDto = {
			[role + 's']: ids
		};

		return this.http
			.delete<DefaultResponse<PublicEncodeJob>>(`${environment.apiUrl}/projects/${project.id}/review/job/${encodeJob.id}/viewers`, {
				body: params
			})
			.pipe(
				tap(response => {
					this.projectsStore.upsert(response.data.projectId, {
						encodeJobs: arrayUpsert(
							this.projectsQuery.getEntity(response.data.projectId).encodeJobs,
							response.data.id,
							response.data
						)
					});
					this.projectsStore.setLoading(false);
				})
			);
	}

	public downloadFromUrl(taskId: string) {
		return this.http.get(`${environment.apiUrl}/encode/task/${taskId}/download`, { responseType: 'blob' });
	}

	public downloadFromUrlWithSse(taskId: string) {
		const url = `${environment.apiUrl}/encode/task/${taskId}/download`;
		return from(this.sse.request<{ resourceKey: string }>(url)).pipe(
			switchMap(({ resourceKey }) => {
				const downloadUrl = `${environment.apiUrl}/encode/job/download-resource/${resourceKey}`;
				return this.http.get(downloadUrl, { responseType: 'blob' });
			})
		);
	}

	public bulkGetFromUrl(jobId: string, page?: number, limit?: number, accessKey?: string) {
		let query = {};
		if (page) query['page'] = page;
		if (limit) query['limit'] = limit;
		return this.http.get(`${environment.apiUrl}/encode/job/${jobId}/download/${accessKey || ''}`, {
			responseType: 'blob',
			params: query
		});
	}

	public bulkGetFromUrlWithSse(jobId: string, page?: number, limit?: number, accessKey?: string, authKey?: string) {
		let query = {};
		if (page) query['page'] = page;
		if (limit) query['limit'] = limit;
		if (authKey) query['auth'] = encodeURIComponent(authKey);

		const queryString = qs.stringify(query, { addQueryPrefix: true, encode: false });
		const url = `${environment.apiUrl}/encode/job/${jobId}/download/${accessKey || ''}${queryString}`;

		return from(this.sse.request<{ resourceKey: string }>(url)).pipe(
			switchMap(({ resourceKey }) => {
				const downloadUrl = `${environment.apiUrl}/encode/job/download-resource/${resourceKey}`;
				return this.http.get(downloadUrl, { responseType: 'blob' });
			})
		);
	}

	public bulkDownloadWithSse(jobId: string, page?: number, limit?: number, accessKey?: string, authKey?: string) {
		let query = {};
		if (page) query['page'] = page;
		if (limit) query['limit'] = limit;
		if (authKey) query['auth'] = encodeURIComponent(authKey);

		const queryString = qs.stringify(query, { addQueryPrefix: true, encode: false });
		const url = `${environment.apiUrl}/encode/job/${jobId}/download/${accessKey || ''}/request${queryString}`;

		return from(this.sse.request<{ resourceKey: string }>(url)).pipe(
			switchMap(({ resourceKey }) => {
				const downloadUrl = `${environment.apiUrl}/encode/job/download-public-resource/${resourceKey}`;
				return this.http.get(downloadUrl, { responseType: 'blob' });
			})
		);
	}

	public downloadExport(jobId: string, page?: number, limit?: number, accessKey?: string, customFilenameTemplate?: string) {
		const query: any = {};
		if (page) query['page'] = page;
		if (limit) query['limit'] = limit;
		if (customFilenameTemplate) query['customFilenameTemplate'] = customFilenameTemplate;

		const queryString = qs.stringify(query, { addQueryPrefix: true, encode: false });
		const requestUrl = `${environment.apiUrl}/encode/job/${jobId}/download/request/${accessKey || ''}${queryString}`;

		return this.http.get<{ encodeTaskId: string }>(requestUrl).pipe(
			switchMap(({ encodeTaskId }) => {
				return this.checkDownloadExportTaskStatus(encodeTaskId);
			}),
			switchMap((encodeTaskId: string) => {
				return this.http.get(`${environment.apiUrl}/encode/task/${encodeTaskId}/download`, { responseType: 'blob' });
			})
		);
	}

	private checkDownloadExportTaskStatus(encodeTaskId: string) {
		const downloadUrl = `${environment.apiUrl}/encode/task/${encodeTaskId}`;

		return this.http
			.get(downloadUrl)
			.pipe(this.validateDownloadExportTaskCompletion(encodeTaskId), this.retryIfDownloadExportTaskPending());
	}

	private validateDownloadExportTaskCompletion(encodeTaskId: string) {
		return switchMap(({ status }: any) => {
			if (status === TaskStatus.Completed) return of(encodeTaskId);
			else return throwError(() => new Error(status));
		});
	}

	private retryIfDownloadExportTaskPending() {
		const retryTimeInMs = 1000;

		return retryWhen(errors =>
			errors.pipe(
				switchMap(error => {
					if (error.message === TaskStatus.Pending || error.message === TaskStatus.Active) return timer(retryTimeInMs);
					else if (error.message === TaskStatus.Failed) return throwError(() => new Error('Task failed'));
				})
			)
		);
	}

	public getFilteredItems(
		jobId: string,
		params: { cartItemId?: CartItem['id']; groupId?: string; packageId?: string; format?: ExportType } = {},
		accessKey?: string
	) {
		return this.http.get(`${environment.apiUrl}/encode/job/${jobId}/filtered/download/${accessKey || ''}`, {
			params: { ...removeObjectValues(params) },
			responseType: 'blob'
		});
	}

	public getFilteredItemsSse(
		jobId: string,
		params: { cartItemId?: CartItem['id']; packageId?: string; format?: ExportType } = {},
		accessKey?: string
	) {
		const queryString = qs.stringify(params, { addQueryPrefix: true, encode: false });
		const url = `${environment.apiUrl}/encode/job/${jobId}/filtered/download/${accessKey || ''}${queryString}`;

		return from(this.sse.request<{ resourceKey: string }>(url)).pipe(
			switchMap(({ resourceKey }) => {
				const downloadUrl = `${environment.apiUrl}/encode/job/download-resource/${resourceKey}`;
				return this.http.get(downloadUrl, { responseType: 'blob' });
			})
		);
	}

	public CSVGetFromUrl(jobId: string) {
		return this.http.get(`${environment.apiUrl}/encode/job/${jobId}/download-csv`, { responseType: 'blob' });
	}

	public downloadExportFromBlob(data: Blob, filename?: string) {
		if (!filename) {
			let date = new Date().getFullYear() + '-' + (new Date().getMonth() + 1) + '-' + new Date().getDate();
			date = date.concat(' at ' + new Date().getHours() + '-' + new Date().getMinutes() + '-' + new Date().getSeconds());

			filename = 'export-' + date;
		}

		const url = this.document.defaultView.URL.createObjectURL(data);
		const link = this.document.createElement('a');
		link.href = url;
		link.download = stringToFilename(filename);
		link.click();
		this.document.defaultView.URL.revokeObjectURL(url);
	}

	public exportCollectionEntity(entity: any) {
		return this.bulkGetFromUrlWithSse(entity.id).subscribe({
			next: (data: Blob) => {
				this.downloadExportFromBlob(data);
			},
			error: () => {
				this.globalService.triggerErrorMessage(
					undefined,
					$localize`:Error Message@@collectionContainerExportEntityErrorMessageText:There was an error downloading the export.`
				);
			}
		});
	}

	/**
	 * Admin-only endpoint to get analytics data for exports
	 * Used by the Export Monitor dashboard analytics section
	 */
	public getAdminAnalytics(range: '7d' | '30d' | '90d' = '30d') {
		return this.http.get<AdminAnalyticsResponse>(`${environment.apiUrl}/encode/admin/analytics`, { params: { range } });
	}

	/**
	 * Admin-only endpoint to get duration analytics for exports
	 * Used by the Export Monitor dashboard performance tab
	 */
	public getDurationAnalytics(range: '7d' | '30d' | '90d' = '30d') {
		return this.http.get<DurationAnalyticsResponse>(`${environment.apiUrl}/encode/admin/analytics/duration`, { params: { range } });
	}

	/**
	 * Admin-only endpoint to get export details regardless of status
	 * Used by the Export Monitor dashboard export detail dialog
	 */
	public getAdminExportDetails(encodeJobId: string) {
		return this.http
			.get<DefaultResponse<AdminExportDetails>>(`${environment.apiUrl}/encode/admin/exports/${encodeJobId}`)
			.pipe(map(response => response.data));
	}

	/**
	 * Admin-only endpoint to list all exports across projects with filtering and pagination
	 * Used by the Export Monitor dashboard
	 */
	public getAdminExports(
		params: {
			page?: number;
			limit?: number;
			status?: 'completed' | 'failed' | 'pending' | 'active' | 'all';
			sortBy?: string;
			sortOrder?: 'ASC' | 'DESC';
			range?: '7d' | '30d' | '90d' | 'all';
		} = {}
	) {
		const queryParams: Record<string, string | number> = {};
		if (params.page) queryParams['page'] = params.page;
		if (params.limit) queryParams['limit'] = params.limit;
		if (params.status && params.status !== 'all') queryParams['status'] = params.status;
		if (params.sortBy) queryParams['sortBy'] = params.sortBy;
		if (params.sortOrder) queryParams['sortOrder'] = params.sortOrder;
		if (params.range && params.range !== 'all') queryParams['range'] = params.range;

		return this.http.get<{
			status: string;
			data: AdminExportItem[];
			page: number;
			limit: number;
			totalResults: number;
			totalPages: number;
		}>(`${environment.apiUrl}/encode/admin/exports`, { params: queryParams });
	}

	public getAdminDebugInfoHtml(exportId: string, taskId: string) {
		const resourceUrl = `${environment.apiUrl}/encode/job/${exportId}/task/${taskId}/debug`;
		return this.http.get(resourceUrl, { responseType: 'text' });
	}
}

/** Interface for admin export list items - matches API response structure */
export interface AdminExportItem {
	id: string;
	created: string;
	status: 'pending' | 'active' | 'completed' | 'failed';
	tag: string;
	project: {
		id: string;
		name: string;
	} | null;
	user: {
		id: string;
		email: string;
		firstName: string | null;
		lastName: string | null;
	} | null;
	taskStats: {
		total: number;
		completed: number;
		failed: number;
		pending: number;
		active: number;
	};
	errorSummary: string[];
}

/** Interface for admin analytics response */
export interface AdminAnalyticsResponse {
	status: string;
	range: '7d' | '30d' | '90d';
	summary: AdminAnalyticsSummary;
	timeSeries: AdminAnalyticsTimeBucket[];
	topErrors: AdminAnalyticsError[];
}

export interface AdminAnalyticsSummary {
	totalExports: number;
	successfulExports: number;
	failedExports: number;
	successRate: number;
	avgDuration?: number;
}

export interface AdminAnalyticsTimeBucket {
	date: string;
	total: number;
	successful: number;
	failed: number;
	avgDuration?: number;
}

export interface AdminAnalyticsError {
	message: string;
	count: number;
	lastSeen: string;
}

/** Interface for admin export details response */
export interface AdminExportDetails {
	id: string;
	status: string;
	created: string;
	projectId: string;
	projectName?: string;
	encodeTasks: AdminExportTask[];
}

export interface AdminExportTask {
	id: string;
	status: string;
	filename?: string;
	error?: string;
	metadata?: {
		name?: string;
		format?: string;
		isVideo?: boolean;
		isAd?: boolean;
		width?: number;
		height?: number;
	};
}

/** Interface for duration analytics response */
export interface DurationAnalyticsResponse {
	status: string;
	range: '7d' | '30d' | '90d';
	avgDuration: number;
	medianDuration: number;
	p95Duration: number;
	p99Duration: number;
	totalExports: number;
	slowExports: SlowExport[];
}

/** Interface for slow export entries */
export interface SlowExport {
	id: string;
	projectName: string;
	duration: number;
	percentAboveAvg: number;
	taskCount: number;
	created: string;
}
